import { useState, useEffect, useRef } from 'react';
import { seriesAPI, divisionsAPI, groundsAPI, teamsAPI, fixturesAPI } from '../../services/api';
import { PageHeader, StatCards, SectionTitle, Field, Spinner, EmptyState } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import { SingleDatePicker } from '../../components/DateRangePicker';
import toast from 'react-hot-toast';

const FORMAT_OPTIONS = [
  {
    key: 'ipl_style',
    label: 'Top 4 Knockout',
    desc: 'Qualifier 1 (1v2), Eliminator (3v4), Qualifier 2, Final. Top 2 get 2 chances.',
    rounds: ['Qualifier 1', 'Eliminator', 'Qualifier 2', 'Final'],
    min_teams: 4,
  },
  {
    key: 'pool_qf',
    label: 'Pool Winners + Eliminators',
    desc: 'Pool winners go direct to QF. Rest play eliminators (2v7, 3v6, 4v5). Your T20 format.',
    rounds: ['Eliminator ×3', 'Quarter Final ×3', 'Semi Final ×2', 'Final'],
    min_teams: 7,
  },
  {
    key: 'top2_direct',
    label: 'Top 2 Direct Final',
    desc: 'Top 2 teams go straight to the final. Simple 2-team knockout.',
    rounds: ['Final'],
    min_teams: 2,
  },
];

const ROUND_COLORS = {
  Qualifier1: 'var(--accent)',  Qualifier2: 'var(--accent)',
  Eliminator: 'var(--gold)',    QF: 'var(--blue)',
  SF: 'var(--purple)',          Final: 'var(--red)',
  League: 'var(--mid)',
};

export default function PlayoffsPage() {
  const { canEdit } = useAuthStore();
  const [seriesList, setSeriesList]   = useState([]);
  const [divisions, setDivisions]     = useState([]);
  const [grounds, setGrounds]         = useState([]);
  const [teams, setTeams]             = useState([]);
  const [selSeries, setSelSeries]     = useState('');
  const [selDivision, setSelDivision] = useState('');
  const [config, setConfig]           = useState(null);
  const [fixtures, setFixtures]       = useState([]);
  const [ranked, setRanked]           = useState([]);
  const [standings, setStandings]     = useState([]); // loaded from DB
  const [standingsEdits, setStandingsEdits] = useState({}); // teamId -> {points, nrr, played, won, lost}
  const [savingStandings, setSavingStandings] = useState(false);
  const [importing, setImporting]           = useState(false);
  const [importResult, setImportResult]     = useState(null); // { matched, unmatched, errors }
  const fileInputRef                        = useRef(null);
  const [loading, setLoading]         = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [saving, setSaving]           = useState(false);
  const [activeTab, setActiveTab]     = useState('setup');
  const [pageView, setPageView]       = useState('view'); // 'view' | 'edit'

  // Config form
  const [format, setFormat]             = useState('pool_qf');
  const [leagueGames, setLeagueGames]   = useState('9');
  const [elimPairs, setElimPairs]       = useState([[2,7],[3,6],[4,5]]);
  const [directQF, setDirectQF]         = useState('1'); // how many per pool go direct
  const [startDate, setStartDate]       = useState('');
  const [timeSlot, setTimeSlot]         = useState('10:00 AM');
  const [groundId, setGroundId]         = useState('');
  const [note, setNote]                 = useState('');

  useEffect(() => {
    seriesAPI.list().then(r => { setSeriesList(r.data); if (r.data.length) setSelSeries(r.data[0].id); }).catch(() => {});
    groundsAPI.list().then(r => setGrounds(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selSeries) return;
    divisionsAPI.list(selSeries).then(r => { setDivisions(r.data); if (r.data.length) setSelDivision(r.data[0].id); }).catch(() => {});
    teamsAPI.list({ series_id: selSeries }).then(r => setTeams(r.data)).catch(() => {});
  }, [selSeries]);

  useEffect(() => { if (selSeries) { loadStatus(); loadStandings(); } }, [selSeries, selDivision]);

  const loadStandings = async () => {
    try {
      const { data } = await api.get(`/standings`, { params: { series_id: selSeries, division_id: selDivision || undefined } });
      setStandings(data);
      // Pre-fill edits from existing standings
      const edits = {};
      data.forEach(s => {
        edits[s.team_id] = { points: s.points, nrr: s.nrr, played: s.played, won: s.won, lost: s.lost, tied: s.tied };
      });
      setStandingsEdits(edits);
    } catch {}
  };

  const saveStandings = async () => {
    setSavingStandings(true);
    try {
      const rows = teams
        .filter(t => !selDivision || t.division_id === selDivision)
        .map(t => {
          const e = standingsEdits[t.id] || {};
          return {
            team_id: t.id,
            team_name: t.name,
            played:    parseInt(e.played)  || 0,
            won:       parseInt(e.won)     || 0,
            lost:      parseInt(e.lost)    || 0,
            tied:      parseInt(e.tied)    || 0,
            no_result: 0,
            points:    parseFloat(e.points) || 0,
            nrr:       parseFloat(e.nrr)    || 0,
          };
        })
        .filter(r => r.points > 0 || r.played > 0); // skip completely empty rows

      if (rows.length === 0) return toast.error('Enter standings for at least one team');

      await api.post('/cricclubs/import/standings', { local_series_id: selSeries, rows });
      toast.success(`Standings saved for ${rows.length} teams`);
      loadStandings();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSavingStandings(false); }
  };

  const setEdit = (teamId, field, value) => {
    setStandingsEdits(p => ({ ...p, [teamId]: { ...p[teamId], [field]: value } }));
  };

  const handleImportExcel = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selSeries) return toast.error('Select a series first');
    setImporting(true);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const base64 = ev.target.result.split(',')[1];
        const { data } = await api.post('/standings/import-excel', {
          series_id: selSeries,
          division_id: selDivision || undefined,
          fileData: base64,
        });
        setImportResult(data);
        if (data.matched > 0) {
          toast.success(`Imported standings for ${data.matched} team${data.matched !== 1 ? 's' : ''}`);
          loadStandings();
        }
        if (data.unmatched?.length > 0) toast.error(`${data.unmatched.length} team(s) not matched`);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Import failed');
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/playoffs/status/${selSeries}`);
      setFixtures(data.fixtures || []);
      if (data.config) {
        setConfig(data.config);
        setFormat(data.config.format || 'pool_qf');
        setLeagueGames(String(data.config.league_games || 9));
        setElimPairs(data.config.eliminator_pairs || [[2,7],[3,6],[4,5]]);
        setDirectQF(String(data.config.direct_qf_per_pool || 1));
        setNote(data.config.note || '');
      }
    } catch {}
    finally { setLoading(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const cfg = { format, league_games: parseInt(leagueGames), eliminator_pairs: elimPairs, direct_qf_per_pool: parseInt(directQF), note };
      await api.put(`/playoffs/config/${selSeries}`, { config: cfg });
      setConfig(cfg);
      toast.success('Playoff format saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const generate = async (replaceExisting = false) => {
    if (!startDate) return toast.error('Set a start date for playoff fixtures');
    setGenerating(true);
    try {
      const { data } = await api.post(`/playoffs/generate/${selSeries}`, {
        division_id: selDivision || null,
        start_date: startDate,
        time_slot: timeSlot,
        ground_id: groundId || null,
        replace_existing: replaceExisting,
      });
      setRanked(data.ranked || []);
      toast.success(`Generated ${data.created} playoff fixtures`);
      loadStatus();
      setActiveTab('fixtures');
    } catch (err) { toast.error(err.response?.data?.error || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const updateFixture = async (id, patch) => {
    try {
      await api.patch(`/playoffs/fixture/${id}`, patch);
      toast.success('Updated');
      loadStatus();
    } catch { toast.error('Update failed'); }
  };

  const playoffFixtures = fixtures.filter(f => f.match_type !== 'League');
  const roundOrder = ['Qualifier1','Eliminator','Qualifier2','QF','SF','Final'];
  const groupedFixtures = roundOrder.reduce((acc, r) => {
    const rf = playoffFixtures.filter(f => f.playoff_round === r || f.match_type === r);
    if (rf.length) acc[r] = rf;
    return acc;
  }, {});

  const selectedFormat = FORMAT_OPTIONS.find(f => f.key === format);

  const cards = [
    { label: 'Format',         value: selectedFormat?.label || '—',         sub: 'Selected format',        color: 'var(--accent)' },
    { label: 'League Games',   value: leagueGames,                          sub: 'Before playoffs start',  color: 'var(--blue)' },
    { label: 'Playoff Rounds', value: playoffFixtures.length,               sub: 'Fixtures generated',     color: 'var(--purple)' },
    { label: 'Status',         value: config ? 'Configured' : 'Not set',    sub: 'Playoff setup',          color: config ? 'var(--accent)' : 'var(--dim)' },
  ];

  return (
    <div>
      <PageHeader icon="🏆" title={pageView === 'view' ? 'Playoffs' : 'Edit Playoffs'}
        subtitle="Configure knockout stage format and generate playoff fixtures."
        actions={
          <div style={{ display:'flex', gap:8 }}>
            {pageView === 'view' ? (
              <>
                {selSeries && (
                  <button className="btn btn-ghost" onClick={() => window.open(fixturesAPI.exportPlayoffUrl(selSeries, selDivision), '_blank')}>
                    📊 Export Excel
                  </button>
                )}
                {canEdit() && <button className="btn btn-primary" onClick={() => setPageView('edit')}>✎ Edit Playoffs</button>}
              </>
            ) : (
              <>
                <button className="btn btn-ghost" onClick={() => setPageView('view')}>← Back</button>
                <button className="btn btn-primary" onClick={saveConfig} disabled={saving}>
                  {saving ? <><Spinner size={14} /> Saving…</> : '✓ Save'}
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Series/Division selector */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="Series" style={{ margin: 0 }}>
          <select className="input select" style={{ width: 200 }} value={selSeries} onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
          </select>
        </Field>
        <Field label="Division" style={{ margin: 0 }}>
          <select className="input select" style={{ width: 180 }} value={selDivision} onChange={e => setSelDivision(e.target.value)}>
            <option value="">All Divisions</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
      </div>

      <StatCards cards={cards} />

      {/* ── VIEW MODE ─────────────────────────────────────────────────────── */}
      {pageView === 'view' && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {/* Config summary */}
          {config && (
            <div className="card" style={{ padding:16, borderLeft:'3px solid var(--accent)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <SectionTitle>Format</SectionTitle>
              </div>
              <div style={{ fontSize:13, color:'var(--text)', marginBottom: config.note ? 8 : 0 }}>
                <strong>{FORMAT_OPTIONS.find(f=>f.key===config.format)?.label || config.format}</strong>
                <span style={{ marginLeft:12, color:'var(--mid)' }}>· {config.league_games} league games</span>
                {config.eliminator_pairs?.length > 0 && (
                  <span style={{ marginLeft:12, color:'var(--mid)' }}>
                    · Eliminators: {config.eliminator_pairs.map(([a,b])=>`${a}v${b}`).join(', ')}
                  </span>
                )}
              </div>
              {config.note && <div style={{ fontSize:12, color:'var(--mid)', fontStyle:'italic', marginTop:4 }}>{config.note}</div>}
            </div>
          )}

          {/* Playoff fixtures grouped by round */}
          {loading ? (
            <div className="card" style={{ padding:60, display:'flex', justifyContent:'center' }}><Spinner size={28} /></div>
          ) : playoffFixtures.length === 0 ? (
            <div className="card" style={{ padding:40 }}>
              <EmptyState icon="🏆" title="No playoff fixtures yet"
                description="Configure the format then generate fixtures."
                action={canEdit() && <button className="btn btn-primary" onClick={() => { setPageView('edit'); setActiveTab('generate'); }}>▶ Generate Fixtures</button>}
              />
            </div>
          ) : (
            Object.entries(groupedFixtures).map(([round, rFixtures]) => (
              <div key={round} className="card" style={{ padding:20, borderLeft:`3px solid ${ROUND_COLORS[round]||'var(--accent)'}` }}>
                <SectionTitle color={ROUND_COLORS[round]||'var(--accent)'}>
                  {round.replace(/([A-Z])/g,' $1').trim()} ({rFixtures.length} fixture{rFixtures.length!==1?'s':''})
                </SectionTitle>
                {rFixtures.map(f => {
                  const homeTeam = teams.find(t => t.id === f.home_team_id);
                  const awayTeam = teams.find(t => t.id === f.away_team_id);
                  const ground   = grounds.find(g => g.id === f.ground_id);
                  return (
                    <div key={f.id} style={{ padding:'10px 0', borderBottom:'1px solid var(--border)', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:200 }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>
                          {homeTeam && awayTeam
                            ? <><span style={{color:'var(--accent)'}}>{homeTeam.short_name}</span> <span style={{color:'var(--mid)',fontSize:11}}>vs</span> <span style={{color:'var(--blue)'}}>{awayTeam.short_name}</span></>
                            : <span style={{color:'var(--dim)',fontStyle:'italic'}}>{f.slot_label || f.conflict_notes || 'TBD vs TBD'}</span>
                          }
                        </div>
                        <div style={{ fontSize:11, color:'var(--mid)', marginTop:3 }}>
                          {f.match_date ? new Date(f.match_date).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'}) : 'Date TBD'}
                          {f.match_time && ` · ${f.match_time}`}
                          {ground && ` · ${ground.name}`}
                        </div>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                        color:f.status==='confirmed'?'var(--accent)':f.status==='completed'?'var(--blue)':'var(--gold)' }}>
                        {f.status}
                      </span>
                      {canEdit() && (
                        <button className="btn btn-ghost btn-sm" onClick={() => { setPageView('edit'); setActiveTab('fixtures'); }}>Edit</button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── EDIT MODE ─────────────────────────────────────────────────────── */}
      {pageView === 'edit' && (
        <div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
            {[['setup','⚙ Setup'], ['standings','📊 Standings'], ['generate','▶ Generate'], ['fixtures','📋 Fixtures']].map(([t, label]) => (
              <button key={t} onClick={() => setActiveTab(t)} style={{
                padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                fontWeight: activeTab === t ? 700 : 500,
                color: activeTab === t ? 'var(--accent)' : 'var(--mid)',
                borderBottom: activeTab === t ? '2px solid var(--accent)' : '2px solid transparent',
                fontSize: 13, marginBottom: -1,
              }}>{label}</button>
            ))}
          </div>

          {loading ? (
            <div className="card" style={{ padding: 60, display: 'flex', justifyContent: 'center' }}><Spinner size={28} /></div>
          ) : (
            <>
          {/* ── Setup Tab ── */}
          {activeTab === 'setup' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card" style={{ padding: 24 }}>
                <SectionTitle>Playoff Format</SectionTitle>
                <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20 }}>
                  Choose how the knockout stage works after the league phase.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                  {FORMAT_OPTIONS.map(opt => (
                    <div key={opt.key} onClick={() => setFormat(opt.key)}
                      style={{ padding: '14px 16px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${format === opt.key ? 'var(--accent)' : 'var(--border)'}`, background: format === opt.key ? 'rgba(0,200,150,.06)' : 'var(--surface)', transition: 'all .15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${format === opt.key ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {format === opt.key && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />}
                        </div>
                        <span style={{ fontWeight: 700, fontSize: 14, color: format === opt.key ? 'var(--accent)' : 'var(--text)' }}>{opt.label}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--mid)', marginLeft: 26, marginBottom: 8 }}>{opt.desc}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 26 }}>
                        {opt.rounds.map(r => <span key={r} className="tag tag-blue" style={{ fontSize: 10 }}>{r}</span>)}
                      </div>
                    </div>
                  ))}
                </div>

                <Field label="League Games Before Playoffs" note="After this many games, playoff stage begins">
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input className="input" type="number" value={leagueGames} onChange={e => setLeagueGames(e.target.value)}
                      style={{ width: 100 }} min="1" />
                    <span style={{ fontSize: 12, color: 'var(--mid)' }}>games per team</span>
                  </div>
                </Field>

                {format === 'pool_qf' && (
                  <>
                    <Field label="Pool Winners Advancing Direct to QF" note="Per pool — usually 1">
                      <div style={{ display: 'flex', gap: 8 }}>
                        {['1','2'].map(n => (
                          <button key={n} type="button" className={`btn btn-sm ${directQF === n ? 'btn-primary' : 'btn-ghost'}`}
                            onClick={() => setDirectQF(n)}>{n} per pool</button>
                        ))}
                      </div>
                    </Field>
                    <Field label="Eliminator Matchups" note="Rank vs Rank — e.g. 2nd vs 7th">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {elimPairs.map((pair, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: 'var(--mid)', width: 80 }}>Eliminator {i+1}</span>
                            <input className="input" type="number" value={pair[0]} style={{ width: 60 }} min="1"
                              onChange={e => { const p = [...elimPairs]; p[i] = [parseInt(e.target.value), p[i][1]]; setElimPairs(p); }} />
                            <span style={{ fontSize: 12, color: 'var(--mid)' }}>vs</span>
                            <input className="input" type="number" value={pair[1]} style={{ width: 60 }} min="1"
                              onChange={e => { const p = [...elimPairs]; p[i] = [p[i][0], parseInt(e.target.value)]; setElimPairs(p); }} />
                            {elimPairs.length > 1 && (
                              <button className="btn btn-ghost btn-sm" onClick={() => setElimPairs(p => p.filter((_,j) => j !== i))}>✕</button>
                            )}
                          </div>
                        ))}
                        <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
                          onClick={() => setElimPairs(p => [...p, [p.length + 2, 8 - p.length]])}>+ Add Eliminator</button>
                      </div>
                    </Field>
                  </>
                )}

                <Field label="Note / Rule Description" note="e.g. After 9 games, top team from each pool goes to QF direct">
                  <textarea className="input" rows={3} value={note} onChange={e => setNote(e.target.value)}
                    placeholder="e.g. After 9 league games, Pool A and Pool B winners go directly to QF. 2nd–4th place play Eliminators (2v7, 3v6, 4v5)."
                    style={{ resize: 'vertical' }} />
                </Field>

                {canEdit() && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button className="btn btn-primary" onClick={saveConfig} disabled={saving}>
                      {saving ? <><Spinner size={14} /> Saving…</> : '✓ Save Format'}
                    </button>
                  </div>
                )}
              </div>

              {/* Current config display */}
              {config?.note && (
                <div className="card" style={{ padding: 16, borderLeft: '3px solid var(--accent)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 6 }}>SAVED RULE NOTE</div>
                  <div style={{ fontSize: 13, color: 'var(--text)' }}>{config.note}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Standings Tab ── */}
          {activeTab === 'standings' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── Import from Excel panel ── */}
              <div className="card" style={{ padding: 20, borderLeft: '4px solid var(--accent)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>📥 Import Standings from Excel</div>
                    <div style={{ fontSize: 12, color: 'var(--mid)' }}>
                      Upload a filled-in Excel file to bulk-import standings. Team names must match the portal.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <a
                      href={api.standingsTemplateUrl ? api.standingsTemplateUrl() : `/api/standings/template?token=${localStorage.getItem('michca_token')}`}
                      download="standings_template.xlsx"
                      className="btn btn-ghost"
                      style={{ fontSize: 12, textDecoration: 'none' }}
                    >
                      ⬇ Download Template
                    </a>
                    {canEdit() && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          style={{ display: 'none' }}
                          onChange={handleImportExcel}
                        />
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: 12 }}
                          disabled={importing}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {importing ? <><Spinner size={13} /> Importing…</> : '📤 Upload Excel'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Import result */}
                {importResult && (
                  <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--surface)', borderRadius: 8, fontSize: 12 }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: importResult.unmatched?.length || importResult.errors?.length ? 10 : 0 }}>
                      <span style={{ color: 'var(--accent)', fontWeight: 700 }}>✓ {importResult.matched} matched & saved</span>
                      {importResult.unmatched?.length > 0 && <span style={{ color: 'var(--gold)', fontWeight: 700 }}>⚠ {importResult.unmatched.length} unmatched</span>}
                      {importResult.errors?.length > 0  && <span style={{ color: 'var(--red)', fontWeight: 700 }}>✗ {importResult.errors.length} errors</span>}
                    </div>
                    {importResult.unmatched?.length > 0 && (
                      <div style={{ color: 'var(--gold)', marginBottom: 4 }}>
                        <strong>Unmatched:</strong> {importResult.unmatched.join(', ')}
                      </div>
                    )}
                    {importResult.errors?.length > 0 && (
                      <div style={{ color: 'var(--red)' }}>
                        <strong>Errors:</strong> {importResult.errors.map(e => `${e.team}: ${e.error}`).join('; ')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Manual entry card ── */}
              <div className="card" style={{ padding: 24 }}>
                <SectionTitle>League Standings — Manual Entry</SectionTitle>
                <p style={{ fontSize: 13, color: "var(--mid)", marginBottom: 20 }}>
                  Enter final standings after the league phase. These are used to rank teams and generate playoff fixtures.
                  Teams are grouped by Pool — pool winners go direct to QF, rest play eliminators.
                </p>

                {(() => {
                  const divTeams = teams.filter(t => !selDivision || t.division_id === selDivision);
                  const pools = [...new Set(divTeams.map(t => t.pool).filter(Boolean))].sort();
                  const unpooled = divTeams.filter(t => !t.pool);

                  const renderPool = (label, poolTeams) => (
                    <div key={label} style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {label} — {poolTeams.length} teams
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: 'var(--surface)' }}>
                              {['Team','P','W','L','T','Pts','NRR'].map(h => (
                                <th key={h} style={{ padding: '8px 10px', textAlign: h==='Team'?'left':'center', fontWeight: 700, fontSize: 11, color: 'var(--mid)', borderBottom: '2px solid var(--border)' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {poolTeams
                              .sort((a,b) => (parseFloat(standingsEdits[b.id]?.points)||0) - (parseFloat(standingsEdits[a.id]?.points)||0)
                                         || (parseFloat(standingsEdits[b.id]?.nrr)||0)    - (parseFloat(standingsEdits[a.id]?.nrr)||0))
                              .map((t, i) => {
                                const e = standingsEdits[t.id] || {};
                                const hasData = standings.find(s => s.team_id === t.id);
                                return (
                                  <tr key={t.id} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                                    <td style={{ padding: '6px 10px', fontWeight: 700 }}>
                                      <span style={{ fontSize: 11, color: 'var(--accent)', fontFamily: "'Roboto Mono', monospace", marginRight: 8 }}>{t.short_name}</span>
                                      <span style={{ fontSize: 12, color: 'var(--mid)' }}>{t.name}</span>
                                      {hasData && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--accent)' }}>✓</span>}
                                    </td>
                                    {[['played','P'],['won','W'],['lost','L'],['tied','T']].map(([f]) => (
                                      <td key={f} style={{ padding: '4px 6px', textAlign: 'center' }}>
                                        <input className="input" type="number" min="0" value={e[f]||''} onChange={ev => setEdit(t.id, f, ev.target.value)}
                                          style={{ width: 48, textAlign: 'center', padding: '4px 2px', fontSize: 12 }} placeholder="0" />
                                      </td>
                                    ))}
                                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                                      <input className="input" type="number" min="0" step="0.5" value={e.points||''} onChange={ev => setEdit(t.id, 'points', ev.target.value)}
                                        style={{ width: 56, textAlign: 'center', padding: '4px 2px', fontSize: 12, fontWeight: 700, color: 'var(--accent)' }} placeholder="0" />
                                    </td>
                                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                                      <input className="input" type="number" step="0.001" value={e.nrr||''} onChange={ev => setEdit(t.id, 'nrr', ev.target.value)}
                                        style={{ width: 72, textAlign: 'center', padding: '4px 2px', fontSize: 12 }} placeholder="+0.000" />
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );

                  return (
                    <>
                      {pools.length > 0 ? pools.map(p => renderPool(`Pool ${p}`, divTeams.filter(t => t.pool === p))) : null}
                      {unpooled.length > 0 ? renderPool('All Teams', unpooled) : null}
                      {divTeams.length === 0 && <div style={{ fontSize: 13, color: 'var(--dim)' }}>No teams found for this series/division.</div>}
                    </>
                  );
                })()}

                {canEdit() && (
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 12, color: 'var(--mid)', alignSelf: 'center' }}>
                      {standings.length > 0 ? `${standings.length} teams have saved standings` : 'No standings saved yet'}
                    </span>
                    <button className="btn btn-primary" onClick={saveStandings} disabled={savingStandings}>
                      {savingStandings ? <><Spinner size={14} /> Saving…</> : '✓ Save Standings'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Generate Tab ── */}
          {activeTab === 'generate' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {!config && (
                <div style={{ padding: '12px 16px', background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 8, fontSize: 13, color: 'var(--mid)' }}>
                  ⚠️ No playoff format configured yet. Go to <strong>Setup</strong> tab first.
                </div>
              )}
              {standings.length === 0 && (
                <div style={{ padding: '12px 16px', background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 8, fontSize: 13, color: 'var(--mid)' }}>
                  ⚠️ No standings found. Go to the <button className="btn btn-ghost" style={{ fontSize: 13, padding: '0 4px', display: 'inline' }} onClick={() => setActiveTab('standings')}><strong>Standings</strong></button> tab and enter final league standings first.
                </div>
              )}

              <div className="card" style={{ padding: 24 }}>
                <SectionTitle>Generate Playoff Fixtures</SectionTitle>
                <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20 }}>
                  Reads the current standings, ranks teams, and creates placeholder fixtures for each playoff round.
                  You can assign actual teams, dates and grounds after generation.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                  <Field label="Playoff Start Date" required>
                    <SingleDatePicker value={startDate} onChange={setStartDate} width="100%" />
                  </Field>
                  <Field label="Default Time Slot">
                    <input className="input" value={timeSlot} onChange={e => setTimeSlot(e.target.value)} placeholder="e.g. 10:00 AM" />
                  </Field>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Default Ground" note="Optional — can be assigned per fixture later">
                      <select className="input select" value={groundId} onChange={e => setGroundId(e.target.value)}>
                        <option value="">No default ground</option>
                        {grounds.filter(g => g.ground_type === 'premier').map(g => <option key={g.id} value={g.id}>★ {g.name}</option>)}
                        {grounds.filter(g => g.ground_type !== 'premier').map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>

                {/* Standings preview */}
                {ranked.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <SectionTitle color="var(--blue)">Rankings Used for Generation</SectionTitle>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                      {ranked.map(r => (
                        <div key={r.rank} style={{ padding: '8px 12px', borderRadius: 7, background: 'var(--surface)', border: `1px solid ${r.rank <= 2 ? 'var(--accent)' : 'var(--border)'}`, display: 'flex', gap: 10, alignItems: 'center' }}>
                          <span style={{ fontSize: 18, fontWeight: 800, color: r.rank === 1 ? 'var(--gold)' : r.rank === 2 ? 'var(--mid)' : 'var(--dim)', minWidth: 24 }}>#{r.rank}</span>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 12 }}>{r.short_name}</div>
                            <div style={{ fontSize: 10, color: 'var(--mid)' }}>Pool {r.pool} · {r.points}pts</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" onClick={() => generate(false)} disabled={generating || !config || !startDate}>
                    {generating ? <><Spinner size={14} /> Generating…</> : '▶ Generate Fixtures'}
                  </button>
                  {playoffFixtures.length > 0 && (
                    <button className="btn btn-ghost" style={{ color: 'var(--red)' }}
                      onClick={() => { if (window.confirm('Replace all existing playoff fixtures?')) generate(true); }}
                      disabled={generating}>
                      ↺ Regenerate (replace existing)
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Fixtures Tab ── */}
          {activeTab === 'fixtures' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {playoffFixtures.length === 0 ? (
                <div className="card" style={{ padding: 40 }}>
                  <EmptyState icon="🏆" title="No playoff fixtures yet"
                    description="Configure the format in Setup, then generate fixtures."
                    action={<button className="btn btn-primary" onClick={() => setActiveTab('generate')}>▶ Generate Fixtures</button>}
                  />
                </div>
              ) : (
                Object.entries(groupedFixtures).map(([round, rFixtures]) => (
                  <div key={round} className="card" style={{ padding: 20, borderLeft: `3px solid ${ROUND_COLORS[round] || 'var(--accent)'}` }}>
                    <SectionTitle color={ROUND_COLORS[round] || 'var(--accent)'}>
                      {round.replace(/([A-Z])/g, ' $1').trim()} ({rFixtures.length} fixture{rFixtures.length > 1 ? 's' : ''})
                    </SectionTitle>
                    {rFixtures.map(f => (
                      <FixtureRow key={f.id} fixture={f} teams={teams} grounds={grounds}
                        canEdit={canEdit()} onUpdate={(patch) => updateFixture(f.id, patch)} />
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
        </div>
      )}
    </div>
  );
}

// ─── Fixture Row ──────────────────────────────────────────────────────────────
function FixtureRow({ fixture: f, teams, grounds, canEdit, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [patch, setPatch]     = useState({
    home_team_id: f.home_team_id || '',
    away_team_id: f.away_team_id || '',
    match_date:   f.match_date ? f.match_date.split('T')[0] : '',
    match_time:   f.match_time || '',
    ground_id:    f.ground_id || '',
    status:       f.status || 'scheduled',
    slot_label:   f.slot_label || '',
  });

  const homeTeam = teams.find(t => t.id === f.home_team_id);
  const awayTeam = teams.find(t => t.id === f.away_team_id);
  const ground   = grounds.find(g => g.id === f.ground_id);

  const save = async () => {
    await onUpdate({
      ...patch,
      home_team_id: patch.home_team_id || null,
      away_team_id: patch.away_team_id || null,
      ground_id:    patch.ground_id || null,
    });
    setEditing(false);
  };

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      {!editing ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {homeTeam && awayTeam
                ? <><span style={{ color: 'var(--accent)' }}>{homeTeam.short_name}</span> vs <span style={{ color: 'var(--blue)' }}>{awayTeam.short_name}</span></>
                : <span style={{ color: 'var(--dim)', fontStyle: 'italic' }}>{f.slot_label || f.conflict_notes || 'TBD vs TBD'}</span>
              }
            </div>
            <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 3 }}>
              {f.match_date ? new Date(f.match_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Date TBD'}
              {f.match_time && ` · ${f.match_time}`}
              {ground && ` · ${ground.name}`}
            </div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            color: f.status === 'confirmed' ? 'var(--accent)' : f.status === 'completed' ? 'var(--blue)' : 'var(--gold)' }}>
            {f.status}
          </span>
          {canEdit && (
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit</button>
          )}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', marginBottom: 10 }}>
            EDIT: {f.slot_label || f.conflict_notes}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Field label="Home Team">
              <select className="input select" value={patch.home_team_id} onChange={e => setPatch(p => ({...p, home_team_id: e.target.value}))}>
                <option value="">TBD</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.short_name} — {t.name}</option>)}
              </select>
            </Field>
            <Field label="Away Team">
              <select className="input select" value={patch.away_team_id} onChange={e => setPatch(p => ({...p, away_team_id: e.target.value}))}>
                <option value="">TBD</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.short_name} — {t.name}</option>)}
              </select>
            </Field>
            <Field label="Date">
              <SingleDatePicker value={patch.match_date} onChange={v => setPatch(p => ({...p, match_date: v}))} width="100%" />
            </Field>
            <Field label="Time">
              <input className="input" value={patch.match_time} onChange={e => setPatch(p => ({...p, match_time: e.target.value}))} placeholder="e.g. 10:00 AM" />
            </Field>
            <Field label="Ground">
              <select className="input select" value={patch.ground_id} onChange={e => setPatch(p => ({...p, ground_id: e.target.value}))}>
                <option value="">TBD</option>
                {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className="input select" value={patch.status} onChange={e => setPatch(p => ({...p, status: e.target.value}))}>
                {['scheduled','confirmed','completed','postponed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Match Label" note="e.g. Pool A Winner vs Eliminator 1 Winner">
            <input className="input" value={patch.slot_label} onChange={e => setPatch(p => ({...p, slot_label: e.target.value}))} />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={save}>✓ Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
